// src/context/AuthContext.tsx
import { createContext, useContext } from "react";
import {
  QueryClient,
  QueryClientProvider
} from "@tanstack/react-query";

// src/network/index.ts
import axios from "axios";

// src/network/configuration.ts
var session = "/session";
var auth = {
  login: "/user/login",
  validate: "/user/validate",
  logout: "/user/logout"
};
var organizations = {
  current: "/session/organization",
  all: "/session/organizations",
  switch: "/session/switchOrganization/"
};
var applications = {
  all: "/session/applications"
};

// src/network/index.ts
var _Requests = class {
  constructor() {
    this.setBearerToken = (token) => {
      this.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    };
    this.signIn = (email) => this.axios.post(auth.login, { emailAddress: email }).then(
      (response) => response.data
    );
    this.getSession = () => this.axios.get(session).then(
      (response) => response.data
    );
    this.signOut = () => this.axios.delete(auth.logout);
    this.validate = async (token) => {
      const config = {
        headers: {
          Authorization: `Bearer ${token} `
        }
      };
      return this.axios.get(`${auth.validate} `, config).then(
        (response) => response.data
      );
      ;
    };
    this.getOrganization = () => this.axios.get(organizations.current).then(
      (response) => response.data
    );
    this.getOrganizations = () => this.axios.get(organizations.all).then(
      (response) => response.data
    );
    this.getApplications = () => this.axios.get(applications.all).then(
      (response) => response.data
    );
    this.switchOrganization = (organizationId) => this.axios.patch(`${organizations.switch}${organizationId}`).then(
      (response) => response.data
    );
    this.axios = axios.create({
      baseURL: process.env.REACT_APP_AUTH_SERVICES_API
    });
    if (process.env.REACT_APP_AUTH_DEV !== void 0 && process.env.REACT_APP_AUTH_DEV === "true") {
      this.axios.defaults.withCredentials = true;
    }
    if (process.env.REACT_APP_AUTH_CROSS_ORIGIN_REQUESTS_ENABLED !== void 0 && process.env.REACT_APP_AUTH_CROSS_ORIGIN_REQUESTS_ENABLED === "true") {
      this.axios.defaults.withCredentials = true;
    }
    this.axios.defaults.timeout = 35e3;
  }
};
var Requests = _Requests;
Requests.getInstance = () => {
  if (!_Requests.client) {
    _Requests.client = new _Requests();
  }
  return _Requests.client;
};
var network_default = Requests;

// src/lib/auth.ts
var _PannaAuthImpl = class {
  constructor() {
    this.signIn = async (email) => {
      return await network_default.getInstance().signIn(email);
    };
    this.currentSession = async () => {
      return await network_default.getInstance().getSession();
    };
    this.validateToken = async (token) => {
      return await network_default.getInstance().validate(token);
    };
    this.signOut = async (options = { redirectToLogin: true }) => {
      const { redirectToLogin } = options;
      await network_default.getInstance().signOut();
      if (redirectToLogin) {
        await queryClient.invalidateQueries({ queryKey: ["session"] });
        this.redirectToSignIn();
      }
    };
  }
  redirectToSignIn(redirectTo) {
    window.location.replace(
      `${process.env.REACT_APP_AUTH_DOMAIN}/login${redirectTo ? `?redirect=${redirectTo}` : ""}`
    );
  }
  redirectToUserProfile() {
    window.location.replace(`${process.env.REACT_APP_AUTH_DOMAIN}/profile`);
  }
  redirectToUserProfileHome() {
    window.location.replace(`${process.env.REACT_APP_AUTH_DOMAIN}`);
  }
  getOrganization() {
    return network_default.getInstance().getOrganization();
  }
  getOrganizations() {
    return network_default.getInstance().getOrganizations();
  }
  async switchOrganization(organizationId) {
    await network_default.getInstance().switchOrganization(organizationId);
  }
  getApplications() {
    return network_default.getInstance().getApplications();
  }
};
var PannaAuthImpl = _PannaAuthImpl;
PannaAuthImpl.getInstance = () => {
  if (!_PannaAuthImpl.client) {
    _PannaAuthImpl.client = new _PannaAuthImpl();
  }
  return _PannaAuthImpl.client;
};
var pannaAuth = PannaAuthImpl.getInstance();
var auth_default = pannaAuth;

// src/hooks/auth.tsx
import { useQuery } from "@tanstack/react-query";
function useSession() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        const session2 = await auth_default.currentSession();
        return session2;
      } catch (e) {
        return null;
      }
    }
  });
  return {
    session: data,
    loading: isLoading,
    error,
    refetch
  };
}

// src/context/AuthContext.tsx
import { jsx } from "react/jsx-runtime";
var queryClient = new QueryClient();
var AuthContext = createContext(void 0);
var useAuth = () => {
  const context = useContext(AuthContext);
  if (context === void 0) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
var SessionWrapper = ({ children }) => {
  const { session: session2, loading, refetch } = useSession();
  const signOut = async (options) => {
    try {
      await auth_default.signOut(options);
    } catch (e) {
      console.log(e);
    }
  };
  return /* @__PURE__ */ jsx(AuthContext.Provider, {
    value: { getSession: refetch, signOut, authState: session2, pannaAuth: auth_default, loading },
    children
  });
};
var AuthProvider = ({ children }) => /* @__PURE__ */ jsx(QueryClientProvider, {
  client: queryClient,
  children: /* @__PURE__ */ jsx(SessionWrapper, {
    children
  })
});
var AuthContext_default = AuthProvider;

// src/hooks/applications.tsx
import { useContext as useContext2 } from "react";
import { useQuery as useQuery2 } from "@tanstack/react-query";
function useApplications() {
  var _a, _b;
  const context = useContext2(AuthContext);
  const { data, isLoading, error, refetch } = useQuery2({
    queryKey: ["applications", (_b = (_a = context == null ? void 0 : context.authState) == null ? void 0 : _a.userInfo) == null ? void 0 : _b.organizationId],
    queryFn: async () => {
      const data2 = await (context == null ? void 0 : context.pannaAuth.getApplications());
      return data2;
    }
  });
  if (context === void 0) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return {
    applications: data,
    loading: isLoading,
    error,
    refetch
  };
}

// src/components/PrivateRoute/index.tsx
import { Fragment, jsx as jsx2 } from "react/jsx-runtime";
function checkApplicationAccess(applications2, currentHost) {
  var _a;
  return (_a = applications2 == null ? void 0 : applications2.some((app) => {
    const parsedUrl = new URL(app.url);
    return parsedUrl.hostname === currentHost;
  })) != null ? _a : false;
}
function checkAuthDomainAccess(authDomain, currentHost) {
  if (!authDomain)
    return false;
  const parsedAuthDomain = new URL(authDomain);
  return parsedAuthDomain.hostname === currentHost;
}
function PrivateRoute(props) {
  const { component, disableApplicationAccessCheck } = props;
  const context = useAuth();
  const { applications: applications2, loading: applicationsLoading } = useApplications();
  const { loading, authState, pannaAuth: pannaAuth2 } = context;
  if (loading) {
    return null;
  }
  if (authState) {
    if (applicationsLoading) {
      return null;
    }
    if (!disableApplicationAccessCheck) {
      const currentHost = location.hostname;
      let hasApplicationAccess = checkApplicationAccess(applications2, currentHost);
      console.log("Debug: Application access checked:", hasApplicationAccess);
      if (!hasApplicationAccess) {
        hasApplicationAccess = checkAuthDomainAccess(process.env.REACT_APP_AUTH_DOMAIN, currentHost);
        console.log("Debug: Auth domain access checked:", hasApplicationAccess);
      }
      if (process.env.REACT_APP_AUTH_DEV !== void 0 && process.env.REACT_APP_AUTH_DEV === "true") {
        hasApplicationAccess = true;
        console.log("Debug: REACT_APP_AUTH_DEV flag is set. Access granted.");
      }
      console.log("Debug: Final access status:", hasApplicationAccess);
      if (!hasApplicationAccess) {
        console.log("Debug: Redirecting to user profile home...");
        pannaAuth2.redirectToUserProfileHome();
        return null;
      }
    }
    return /* @__PURE__ */ jsx2(Fragment, {
      children: component || null
    });
  }
  pannaAuth2.redirectToSignIn(window.location.href);
  return null;
}
var PrivateRoute_default = PrivateRoute;

// src/access/index.ts
var hasModuleAccess = (accessArray, m) => {
  if (Array.isArray(m)) {
    return m.some((module) => accessArray.some((accessModule) => accessModule.includes(module)));
  }
  return accessArray.some((accessModule) => accessModule.includes(m));
};

// src/access/module-access.tsx
import { Fragment as Fragment2, jsx as jsx3 } from "react/jsx-runtime";
function ModuleAccess({ children, module }) {
  var _a;
  const authContext = useAuth();
  const { authState } = authContext;
  const accessGrantedModules = ((_a = authState == null ? void 0 : authState.userInfo) == null ? void 0 : _a.modules) || [];
  if (hasModuleAccess(accessGrantedModules, module)) {
    return /* @__PURE__ */ jsx3(Fragment2, {
      children
    });
  }
  return /* @__PURE__ */ jsx3("div", {
    className: "flex p-10 justify-center items-center text-primary text-4xl font-bold",
    children: "No Access"
  });
}
var module_access_default = ModuleAccess;

// src/hooks/withModuleAccess.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function withModuleAccess(Component, module) {
  function ComponentWithModuleAccessCheck(props) {
    return /* @__PURE__ */ jsx4(module_access_default, {
      module,
      children: /* @__PURE__ */ jsx4(Component, {
        ...props
      })
    });
  }
  return ComponentWithModuleAccessCheck;
}
var withModuleAccess_default = withModuleAccess;

// src/hooks/withDocumentTitle.tsx
import { useRef, useEffect } from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
function withDocumentTitle(Component, title, prevailOnUnmount = false) {
  function ComponentWithSuspense(props) {
    const defaultTitle = useRef(document.title);
    useEffect(() => {
      document.title = title;
    }, [title]);
    useEffect(() => () => {
      if (!prevailOnUnmount) {
        document.title = defaultTitle.current;
      }
    }, []);
    return /* @__PURE__ */ jsx5(Component, {
      ...props
    });
  }
  return ComponentWithSuspense;
}
var withDocumentTitle_default = withDocumentTitle;

// src/hooks/withSuspense.tsx
import { Suspense } from "react";
import { Fragment as Fragment3, jsx as jsx6 } from "react/jsx-runtime";
function withSuspense(Component, fallback) {
  function ComponentWithSuspense(props) {
    return /* @__PURE__ */ jsx6(Suspense, {
      fallback: fallback || /* @__PURE__ */ jsx6(Fragment3, {
        children: "Loading..."
      }),
      children: /* @__PURE__ */ jsx6(Component, {
        ...props
      })
    });
  }
  return ComponentWithSuspense;
}
var withSuspense_default = withSuspense;

// src/components/OrganizationSwitcher/index.tsx
import { Avatar, ListItemButton, Menu, MenuItem, Tooltip } from "@mui/material";
import * as React4 from "react";
import { twMerge } from "tailwind-merge";

// src/hooks/organizations.tsx
import { useMutation, useQuery as useQuery3 } from "@tanstack/react-query";
import { useContext as useContext3, useEffect as useEffect2, useRef as useRef2, useState } from "react";
function useOrganization() {
  var _a, _b;
  const context = useContext3(AuthContext);
  const { data, isLoading, error, refetch } = useQuery3({
    queryKey: ["organization", (_b = (_a = context == null ? void 0 : context.authState) == null ? void 0 : _a.userInfo) == null ? void 0 : _b.organizationId],
    queryFn: async () => {
      const data2 = await (context == null ? void 0 : context.pannaAuth.getOrganization());
      return data2;
    }
  });
  if (context === void 0) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return {
    organization: data,
    loading: isLoading,
    error,
    refetch
  };
}
function useOrganizations() {
  const context = useContext3(AuthContext);
  const [loading, setLoading] = useState(true);
  const [organizations2, setOrganization] = useState(null);
  const fetchingRef = useRef2(false);
  if (context === void 0) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  const fetchOrganizations = async () => {
    if (fetchingRef.current)
      return;
    try {
      setLoading(true);
      fetchingRef.current = true;
      const organization = await context.pannaAuth.getOrganizations();
      setOrganization(organization);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };
  useEffect2(() => {
    fetchOrganizations();
  }, []);
  return { organizations: organizations2, loading, refetch: fetchOrganizations };
}
function useSwitchOrganization({ reloadOnOrganizationSwitch }) {
  const context = useContext3(AuthContext);
  const { mutateAsync, error, status, isPending } = useMutation({
    mutationFn: async (organizationId) => {
      return await (context == null ? void 0 : context.pannaAuth.switchOrganization(organizationId));
    }
  });
  if (context === void 0) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  const switchOrganization = async (organizationId) => {
    await mutateAsync(organizationId).then(async () => {
      if (reloadOnOrganizationSwitch) {
        window.location.reload();
      } else {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["organization"] }),
          queryClient.invalidateQueries({ queryKey: ["applications"] })
        ]);
      }
    }).catch(() => {
    });
  };
  return {
    switchOrganization,
    loading: isPending,
    error,
    status
  };
}

// src/components/OrganizationSwitcher/index.tsx
import { Fragment as Fragment5, jsx as jsx7, jsxs } from "react/jsx-runtime";
function OrganizationSwither(props) {
  const { iconOnly, iconClassName, reloadOnOrganizationSwitch } = props;
  const { loading, organization } = useOrganization();
  const [previousOrganization, setPreviousOrganization] = React4.useState(void 0);
  const { loading: organizationsLoading, organizations: organizations2 } = useOrganizations();
  const { loading: switching, switchOrganization } = useSwitchOrganization({ reloadOnOrganizationSwitch });
  React4.useEffect(() => {
    if (previousOrganization === void 0) {
      setPreviousOrganization(organization);
    } else if (organization && previousOrganization && organization.id !== previousOrganization.id) {
      if (reloadOnOrganizationSwitch) {
        window.location.reload();
      }
    }
  }, [organization]);
  const [anchorEl, setAnchorEl] = React4.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    if (organizations2 && (organizations2 == null ? void 0 : organizations2.length) > 1) {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  return /* @__PURE__ */ jsxs(React4.Fragment, {
    children: [
      /* @__PURE__ */ jsxs(Fragment5, {
        children: [
          !iconOnly && /* @__PURE__ */ jsx7(Tooltip, {
            title: organizations2 && (organizations2 == null ? void 0 : organizations2.length) > 1 ? "Switch Organization" : null,
            placement: "right",
            children: /* @__PURE__ */ jsxs(ListItemButton, {
              onClick: handleClick,
              className: "pauth-flex pauth-mx-auto pauth-bg-white hover:pauth-bg-slate-50 pauth-justify-between pauth-gap-2 pauth-p-2 pauth-cursor-pointer pauth-w-full pauth-h-full pauth-items-center pauth-shadow-sm",
              id: "organization-switch",
              "aria-haspopup": "listbox",
              "aria-controls": "organization-switch",
              "aria-expanded": open ? "true" : void 0,
              children: [
                loading && /* @__PURE__ */ jsx7("div", {
                  className: "pauth-absolute pauth-inset-0 pauth-flex pauth-justify-center pauth-items-center pauth-bg-[#ffffffb3]",
                  children: "Loading..."
                }),
                /* @__PURE__ */ jsxs("div", {
                  className: "pauth-flex pauth-gap-2 pauth-items-center",
                  children: [
                    /* @__PURE__ */ jsx7(Avatar, {
                      sx: { width: 26, height: 26 },
                      variant: "square",
                      className: "!pauth-bg-white pauth-flex puth-gap-2 !pauth-border-solid pauth-shadow-md",
                      children: /* @__PURE__ */ jsx7("img", {
                        src: organization == null ? void 0 : organization.logo,
                        className: "pauth-object-cover pauth-w-6"
                      })
                    }),
                    /* @__PURE__ */ jsx7("span", {
                      className: "pauth-text-zinc-600",
                      children: organization == null ? void 0 : organization.name
                    })
                  ]
                }),
                organizations2 && (organizations2 == null ? void 0 : organizations2.length) > 1 && /* @__PURE__ */ jsx7("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  "stroke-width": "1.5",
                  stroke: "currentColor",
                  className: twMerge("pauth-w-5 pauth-h-5", iconClassName),
                  children: /* @__PURE__ */ jsx7("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    d: "M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5",
                    className: twMerge(iconClassName)
                  })
                })
              ]
            })
          }),
          iconOnly && /* @__PURE__ */ jsx7(Tooltip, {
            title: organizations2 && (organizations2 == null ? void 0 : organizations2.length) > 1 ? "Switch Organization" : null,
            placement: "right",
            children: /* @__PURE__ */ jsx7(Avatar, {
              id: "organization-switch",
              "aria-haspopup": "listbox",
              "aria-controls": "organization-switch",
              "aria-expanded": open ? "true" : void 0,
              onClick: handleClick,
              sx: { width: 26, height: 26 },
              variant: "square",
              className: "!pauth-bg-white pauth-cursor-pointer pauth-flex puth-gap-2 !pauth-border-solid pauth-shadow-md",
              children: /* @__PURE__ */ jsx7("img", {
                src: organization == null ? void 0 : organization.logo,
                className: "pauth-object-cover pauth-w-6"
              })
            })
          })
        ]
      }),
      /* @__PURE__ */ jsxs(Menu, {
        id: "organization-switch",
        open,
        onClose: handleClose,
        anchorEl,
        MenuListProps: {
          "aria-labelledby": "organization-switch",
          role: "listbox"
        },
        sx: {
          "& .MuiPaper-root": {
            padding: "10px"
          }
        },
        children: [
          !organizationsLoading && (organizations2 == null ? void 0 : organizations2.map((organizationItem) => {
            return /* @__PURE__ */ jsx7(Fragment5, {
              children: /* @__PURE__ */ jsxs(MenuItem, {
                className: twMerge(
                  "pauth-min-w-[300px] pauth-relative",
                  organization && organization.id === organizationItem.id && "pauth-bg-slate-100"
                ),
                onClick: async () => {
                  if (Boolean(organization && organization.id === organizationItem.id))
                    return;
                  await switchOrganization(organizationItem.id);
                  handleClose();
                },
                children: [
                  /* @__PURE__ */ jsxs("div", {
                    className: "pauth-flex pauth-gap-2 pauth-items-center",
                    children: [
                      /* @__PURE__ */ jsx7(Avatar, {
                        className: "!pauth-bg-white pauth-flex pauth-gap-2 !pauth-border-solid pauth-shadow-md",
                        children: /* @__PURE__ */ jsx7("img", {
                          src: organizationItem == null ? void 0 : organizationItem.logo,
                          className: "pauth-object-cover",
                          style: { width: 20 }
                        })
                      }),
                      /* @__PURE__ */ jsx7("span", {
                        className: "pauth-text-zinc-600",
                        children: organizationItem == null ? void 0 : organizationItem.name
                      })
                    ]
                  }),
                  organization && organization.id === organizationItem.id && /* @__PURE__ */ jsx7(Tooltip, {
                    title: "Selected Organization",
                    children: /* @__PURE__ */ jsx7("div", {
                      className: "pauth-w-4 pauth-absolute pauth-right-4 pauth-h-4 pauth-bg-green-500 pauth-rounded-full pauth-z-[100000]"
                    })
                  })
                ]
              })
            });
          })),
          switching && /* @__PURE__ */ jsx7("div", {
            className: "pauth-absolute pauth-inset-0 pauth-flex pauth-justify-center pauth-items-center pauth-bg-[#ffffffb3]",
            children: "Switching..."
          })
        ]
      })
    ]
  });
}

// src/components/ApplicationSwitcher/index.tsx
import { Avatar as Avatar2, ButtonBase, Card, Grid, IconButton, Popover, Tooltip as Tooltip2, Typography } from "@mui/material";
import { twMerge as twMerge3 } from "tailwind-merge";
import React5 from "react";

// src/icons/NewTab.tsx
import { twMerge as twMerge2 } from "tailwind-merge";
import { jsx as jsx8 } from "react/jsx-runtime";
var NewTabIcon = function NewTabIcon2({
  className,
  width,
  height
}) {
  return /* @__PURE__ */ jsx8("svg", {
    className,
    width,
    height,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx8("path", {
      className: twMerge2(className),
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M20 14a1 1 0 0 0-1 1v3.077c0 .459-.022.57-.082.684a.363.363 0 0 1-.157.157c-.113.06-.225.082-.684.082H5.923c-.459 0-.571-.022-.684-.082a.363.363 0 0 1-.157-.157c-.06-.113-.082-.225-.082-.684L4.999 5.5a.5.5 0 0 1 .5-.5l3.5.005a1 1 0 1 0 .002-2L5.501 3a2.5 2.5 0 0 0-2.502 2.5v12.577c0 .76.083 1.185.32 1.627.223.419.558.753.977.977.442.237.866.319 1.627.319h12.154c.76 0 1.185-.082 1.627-.319.419-.224.753-.558.977-.977.237-.442.319-.866.319-1.627V15a1 1 0 0 0-1-1zm-2-9.055v-.291l-.39.09A10 10 0 0 1 15.36 5H14a1 1 0 1 1 0-2l5.5.003a1.5 1.5 0 0 1 1.5 1.5V10a1 1 0 1 1-2 0V8.639c0-.757.086-1.511.256-2.249l.09-.39h-.295a10 10 0 0 1-1.411 1.775l-5.933 5.932a1 1 0 0 1-1.414-1.414l5.944-5.944A10 10 0 0 1 18 4.945z",
      fill: "currentColor"
    })
  });
};
NewTabIcon.defaultProps = {
  className: "",
  width: 24,
  height: 24
};
var NewTab_default = NewTabIcon;

// src/components/ApplicationSwitcher/index.tsx
import { jsx as jsx9, jsxs as jsxs2 } from "react/jsx-runtime";
function ApplicationSwitcher(props) {
  var _a, _b, _c, _d, _e, _f, _g;
  const { openNewUrl, iconClassName } = props;
  const { applications: applications2, loading } = useApplications();
  const { authState } = useAuth();
  const [anchorEl, setAnchorEl] = React5.useState(null);
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "application-switcher" : void 0;
  const openUrl = (url, openNewTab) => {
    if (openNewTab) {
      window.open(url, "_blank") || window.location.replace(url);
    } else {
      window.location.replace(url);
    }
    handlePopoverClose();
  };
  return /* @__PURE__ */ jsxs2("div", {
    children: [
      /* @__PURE__ */ jsx9(Tooltip2, {
        title: "Switch Application",
        placement: "right",
        children: /* @__PURE__ */ jsx9(IconButton, {
          onClick: handlePopoverOpen,
          "aria-describedby": id,
          children: /* @__PURE__ */ jsx9("svg", {
            className: twMerge3("pauth-h-6 pauth-w-6 fill-white", iconClassName),
            focusable: "false",
            viewBox: "0 0 24 24",
            children: /* @__PURE__ */ jsx9("path", {
              d: "M6,8c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM12,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM6,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM6,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM12,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM16,6c0,1.1 0.9,2 2,2s2,-0.9 2,-2 -0.9,-2 -2,-2 -2,0.9 -2,2zM12,8c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM18,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM18,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2z"
            })
          })
        })
      }),
      /* @__PURE__ */ jsx9(Popover, {
        id,
        open,
        anchorEl,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right"
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left"
        },
        onClose: handlePopoverClose,
        sx: {
          "& .MuiPaper-root": {
            padding: "20px"
          }
        },
        children: /* @__PURE__ */ jsxs2(Grid, {
          container: true,
          spacing: 2,
          maxWidth: 250,
          children: [
            /* @__PURE__ */ jsx9(Grid, {
              item: true,
              md: 12,
              onClick: () => openUrl(process.env.REACT_APP_AUTH_DOMAIN, openNewUrl),
              children: /* @__PURE__ */ jsxs2(Card, {
                component: ButtonBase,
                className: "pauth-flex pauth-w-full !pauth-p-2 pauth-gap-1 pauth-items-center pauth-justify-center pauth-flex-col hover:pauth-shadow-xl",
                children: [
                  /* @__PURE__ */ jsx9(Tooltip2, {
                    placement: "top",
                    title: "Open in new tab",
                    children: /* @__PURE__ */ jsx9("div", {
                      onClick: (e) => {
                        e.stopPropagation();
                        openUrl(process.env.REACT_APP_AUTH_DOMAIN, true);
                      },
                      className: "pauth-cursor-pointer  pauth-absolute pauth-top-0  pauth-right-0",
                      children: /* @__PURE__ */ jsx9(NewTab_default, {
                        className: "fill-primary",
                        height: 16,
                        width: 16
                      })
                    })
                  }),
                  /* @__PURE__ */ jsxs2(Avatar2, {
                    sx: { width: 80, height: 80 },
                    src: (_a = authState == null ? void 0 : authState.userInfo) == null ? void 0 : _a.profilePic,
                    variant: "circular",
                    className: "pauth-cursor-pointer pauth-flex pauth-text-3xl",
                    children: [
                      ((_b = authState == null ? void 0 : authState.userInfo) == null ? void 0 : _b.profilePic) && /* @__PURE__ */ jsx9("img", {
                        src: (_c = authState == null ? void 0 : authState.userInfo) == null ? void 0 : _c.profilePic,
                        className: "pauth-object-cover pauth-w-16"
                      }),
                      !((_d = authState == null ? void 0 : authState.userInfo) == null ? void 0 : _d.profilePic) && ((_e = authState == null ? void 0 : authState.userInfo) == null ? void 0 : _e.firstName) && ((_f = authState == null ? void 0 : authState.userInfo) == null ? void 0 : _f.firstName.length) > 0 && ((_g = authState == null ? void 0 : authState.userInfo) == null ? void 0 : _g.firstName[0])
                    ]
                  }),
                  /* @__PURE__ */ jsx9(Typography, {
                    className: "pauth-text-xs",
                    children: "Account"
                  })
                ]
              })
            }),
            applications2 == null ? void 0 : applications2.map((application) => /* @__PURE__ */ jsx9(Grid, {
              item: true,
              md: 6,
              onClick: () => openUrl(application.url, openNewUrl),
              children: /* @__PURE__ */ jsxs2(Card, {
                component: ButtonBase,
                className: "pauth-flex !pauth-p-2 !pauth-px-4 pauth-gap-1 pauth-items-center pauth-justify-center pauth-flex-col  hover:pauth-shadow-xl",
                children: [
                  /* @__PURE__ */ jsx9(Tooltip2, {
                    placement: "top",
                    title: "Open in new tab",
                    children: /* @__PURE__ */ jsx9("div", {
                      onClick: (e) => {
                        e.stopPropagation();
                        openUrl(application.url, true);
                      },
                      className: "pauth-cursor-pointer  pauth-absolute pauth-top-0  pauth-right-0",
                      children: /* @__PURE__ */ jsx9(NewTab_default, {
                        className: "fill-primary",
                        height: 16,
                        width: 16
                      })
                    })
                  }),
                  /* @__PURE__ */ jsx9(Avatar2, {
                    sx: { width: 80, height: 80 },
                    variant: "square",
                    className: "!pauth-bg-white pauth-cursor-pointer pauth-flex",
                    children: /* @__PURE__ */ jsx9("img", {
                      src: application == null ? void 0 : application.icon,
                      className: "pauth-object-cover pauth-w-16"
                    })
                  }),
                  /* @__PURE__ */ jsx9(Typography, {
                    className: "pauth-text-xs",
                    children: application.displayName || application.name
                  })
                ]
              })
            })),
            (!applications2 || (applications2 == null ? void 0 : applications2.length) === 0) && /* @__PURE__ */ jsx9(Grid, {
              item: true,
              md: 12,
              children: /* @__PURE__ */ jsx9(Typography, {
                className: "pauth-text-xs",
                children: "No applications found"
              })
            })
          ]
        })
      })
    ]
  });
}
var ApplicationSwitcher_default = ApplicationSwitcher;

// src/components/ProfileInfo/index.tsx
import { twMerge as twMerge5 } from "tailwind-merge";
import { Avatar as Avatar3, Stack, Tooltip as Tooltip3, Typography as Typography2 } from "@mui/material";

// src/icons/Profile.tsx
import { twMerge as twMerge4 } from "tailwind-merge";
import { jsx as jsx10, jsxs as jsxs3 } from "react/jsx-runtime";
var ProfileIcon = function ProfileIcon2({
  className,
  width,
  height
}) {
  return /* @__PURE__ */ jsxs3("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    className,
    width,
    height,
    viewBox: "0 0 24 24",
    children: [
      /* @__PURE__ */ jsx10("circle", {
        className: twMerge4("", className),
        cx: "12",
        cy: "10",
        r: "3"
      }),
      /* @__PURE__ */ jsx10("circle", {
        className: twMerge4(className, "fill-none"),
        cx: "12",
        cy: "12",
        r: "9",
        strokeWidth: "1.2"
      }),
      /* @__PURE__ */ jsx10("path", {
        className: twMerge4("fill-transparent", className),
        d: "M17.8719 18.8083C17.9489 18.7468 17.9799 18.6436 17.9452 18.5513C17.5693 17.5518 16.8134 16.6706 15.7814 16.0332C14.6966 15.3632 13.3674 15 12 15C10.6326 15 9.30341 15.3632 8.21858 16.0332C7.18663 16.6706 6.43066 17.5518 6.05477 18.5513C6.02009 18.6436 6.05115 18.7468 6.12813 18.8083C9.56196 21.552 14.438 21.552 17.8719 18.8083Z",
        strokeWidth: "1.2",
        strokeLinecap: "round"
      })
    ]
  });
};
ProfileIcon.defaultProps = {
  className: "",
  width: 24,
  height: 24
};
var Profile_default = ProfileIcon;

// src/icons/Seperator.tsx
import { useId } from "react";
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
var SeperatorIcon = function SeperatorIcon2({
  className,
  stopColor,
  width,
  height
}) {
  const id = useId();
  return /* @__PURE__ */ jsxs4("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    className,
    width,
    height,
    viewBox: "0 0 187 1",
    children: [
      /* @__PURE__ */ jsx11("line", {
        y1: "0.5",
        x2: "187",
        y2: "0.5",
        stroke: `url(#${id})`
      }),
      /* @__PURE__ */ jsx11("defs", {
        children: /* @__PURE__ */ jsxs4("radialGradient", {
          id: `${id}`,
          cx: "0",
          cy: "0",
          r: "1",
          gradientUnits: "userSpaceOnUse",
          gradientTransform: "translate(92.9995 1.00034) rotate(-180) scale(70.4995 13183.4)",
          children: [
            /* @__PURE__ */ jsx11("stop", {
              stopColor
            }),
            /* @__PURE__ */ jsx11("stop", {
              offset: "1",
              stopColor,
              stopOpacity: "0"
            })
          ]
        })
      })
    ]
  });
};
SeperatorIcon.defaultProps = {
  className: "",
  width: 187,
  height: 1,
  stopColor: "#D9D9D9"
};
var Seperator_default = SeperatorIcon;

// src/components/ProfileInfo/index.tsx
import { Fragment as Fragment6, jsx as jsx12, jsxs as jsxs5 } from "react/jsx-runtime";
function ProfileInfo(props) {
  var _a, _b, _c, _d, _e, _f, _g;
  const { open } = props;
  const authContext = useAuth();
  const { authState: auth2 } = authContext;
  const profileSelected = location.pathname.includes("/profile");
  const onClickHandler = (href) => {
    window.location.replace(`${process.env.REACT_APP_AUTH_DOMAIN}/profile`);
  };
  return /* @__PURE__ */ jsxs5(Stack, {
    spacing: 2,
    children: [
      /* @__PURE__ */ jsxs5(Stack, {
        direction: "column",
        justifyContent: "center",
        alignItems: "center",
        children: [
          /* @__PURE__ */ jsx12(Tooltip3, {
            title: "My Profile",
            placement: "right",
            children: /* @__PURE__ */ jsx12(Avatar3, {
              sx: { width: open ? 56 : 36, height: open ? 56 : 36 },
              src: (_a = auth2 == null ? void 0 : auth2.userInfo) == null ? void 0 : _a.profilePic,
              onClick: () => onClickHandler("/profile"),
              className: twMerge5("pauth-cursor-pointer", !open && profileSelected && "pauth-outline pauth-outline-primary"),
              children: !((_b = auth2 == null ? void 0 : auth2.userInfo) == null ? void 0 : _b.profilePic) && ((_c = auth2 == null ? void 0 : auth2.userInfo) == null ? void 0 : _c.firstName) && ((_d = auth2 == null ? void 0 : auth2.userInfo) == null ? void 0 : _d.firstName.length) > 0 && ((_e = auth2 == null ? void 0 : auth2.userInfo) == null ? void 0 : _e.firstName[0])
            })
          }),
          open && /* @__PURE__ */ jsxs5(Fragment6, {
            children: [
              /* @__PURE__ */ jsx12(Typography2, {
                variant: "h6",
                children: (_f = auth2 == null ? void 0 : auth2.userInfo) == null ? void 0 : _f.firstName
              }),
              /* @__PURE__ */ jsx12(Typography2, {
                variant: "caption",
                children: (_g = auth2 == null ? void 0 : auth2.userInfo) == null ? void 0 : _g.designation
              })
            ]
          })
        ]
      }),
      open && /* @__PURE__ */ jsxs5("div", {
        className: "mb-10 flex flex-col justify-center items-center gap-1 cursor-pointer",
        onClick: () => onClickHandler("/profile"),
        children: [
          /* @__PURE__ */ jsxs5("div", {
            className: "flex justify-center items-center gap-2",
            children: [
              /* @__PURE__ */ jsx12(Profile_default, {
                className: twMerge5(
                  "fill-secondary stroke-secondary",
                  profileSelected && "fill-primary stroke-primary"
                )
              }),
              open && /* @__PURE__ */ jsx12(Typography2, {
                className: twMerge5(
                  "text-secondary",
                  profileSelected && "text-primary"
                ),
                variant: "caption",
                children: "My Profile"
              })
            ]
          }),
          /* @__PURE__ */ jsx12(Seperator_default, {
            width: open ? 160 : 90,
            stopColor: profileSelected ? "#F97171" : "#D9D9D9"
          })
        ]
      })
    ]
  });
}
var ProfileInfo_default = ProfileInfo;

// src/hooks/withAuth.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
function withAuth(Component) {
  function ComponentWithAuth(props) {
    const context = useAuth();
    return /* @__PURE__ */ jsx13(Component, {
      ...props,
      auth: context
    });
  }
  return ComponentWithAuth;
}
export {
  ApplicationSwitcher_default as ApplicationSwitcher,
  AuthContext,
  AuthContext_default as AuthProvider,
  network_default as AuthRequests,
  OrganizationSwither,
  PrivateRoute_default as PrivateRoute,
  ProfileInfo_default as ProfileInfo,
  hasModuleAccess,
  auth_default as pannaAuth,
  useApplications,
  useAuth,
  useOrganization,
  useOrganizations,
  useSession,
  useSwitchOrganization,
  withAuth,
  withDocumentTitle_default as withDocumentTitle,
  withModuleAccess_default as withModuleAccess,
  withSuspense_default as withSuspense
};
